import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Zap, Loader2, ArrowRight } from "lucide-react";


export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("demo@algoforge.io");
  const [password, setPassword] = useState("Demo@123");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[var(--bg-page)] text-white">
      {/* ── Left panel — hero ─────────────────────────────────── */}
      <div
        className="hidden lg:flex w-[52%] xl:w-[55%] relative"
        style={{
          background: "linear-gradient(135deg, #0b0f14 0%, #111820 50%, #0d1117 100%)",
        }}
      >
        {/* Subtle dot grid overlay */}
        <div className="absolute inset-0 grid-bg opacity-60" />

        <div className="relative z-10 flex flex-col justify-between h-full p-12 xl:p-16">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[var(--accent)] text-white flex items-center justify-center rounded-sm">
              <Zap className="w-[18px] h-[18px]" strokeWidth={2.5} />
            </div>
            <div className="font-display text-xl tracking-tight">ALGOFORGE</div>
          </div>

          {/* Headline */}
          <div className="max-w-lg">
            <div className="text-[10px] text-[var(--text-muted)] tracking-[0.18em] uppercase font-section mb-4">
              AI Trading Operating System
            </div>
            <h1 className="font-display text-[56px] xl:text-[64px] leading-[0.92] mb-6 text-white">
              Build, backtest &amp; defend against option-writer traps.
            </h1>
            <p className="text-[var(--text-secondary)] text-[14px] leading-relaxed max-w-sm">
              Natural-language strategy generation with GPT-4o + Claude.
              Squeeze detection, tick-replay backtests, live broker execution,
              and a real-time cockpit built for Indian equity &amp; options desks.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mt-10">
              {[
                { num: "14+", label: "Broker integrations" },
                { num: "AI", label: "GPT-4o + Claude", color: "txt-profit" },
                { num: "Live", label: "Trap detection", color: "txt-warn" },
              ].map(({ num, label, color }) => (
                <div key={label} className="border border-[var(--border)] p-4 bg-[rgba(255,255,255,0.02)]">
                  <div className={`kpi-num text-[32px] ${color || ""}`}>{num}</div>
                  <div className="text-[9px] text-[var(--text-muted)] tracking-widest uppercase font-section mt-1.5">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer badges */}
          <div className="flex items-center gap-4 flex-wrap">
            {["SEBI-Compliance Ready", "Audit-Trailed", "Sandbox First", "Zero Code Required"].map((b) => (
              <div key={b} className="text-[9px] text-[var(--text-muted)] tracking-widest uppercase font-section flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-[var(--text-muted)] inline-block" />
                {b}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel — form ────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[var(--bg-surface)] lg:bg-[var(--bg-page)]">
        <form onSubmit={submit} className="w-full max-w-[340px]" data-testid="login-form">
          <div className="text-[10px] text-[var(--text-muted)] tracking-[0.18em] uppercase font-section mb-2">Sign in</div>
          <h2 className="font-display text-[38px] leading-tight mb-1 text-[var(--text-primary)]">Access cockpit</h2>
          <p className="text-[var(--text-muted)] text-sm mb-8">Use your trader credentials.</p>

          <div className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-[10px] text-[var(--text-muted)] tracking-[0.14em] uppercase font-section block mb-1.5">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none bg-[var(--bg-surface-2)] border-[var(--border)] font-mono-data text-sm h-11 focus:border-[var(--accent)] focus:ring-0 transition-colors"
                required
                autoComplete="email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="password" className="text-[10px] text-[var(--text-muted)] tracking-[0.14em] uppercase font-section">Password</Label>
                <Link to="/forgot-password" className="text-[11px] text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors">
                  Forgot?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none bg-[var(--bg-surface-2)] border-[var(--border)] font-mono-data text-sm h-11 focus:border-[var(--accent)] focus:ring-0 transition-colors"
                required
                autoComplete="current-password"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full rounded-none bg-[var(--accent)] hover:bg-[#4a4ee0] text-white h-11 font-section tracking-wider text-sm transition-colors duration-150 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>AUTHENTICATING…</span>
                </>
              ) : (
                <>
                  <span>ENTER COCKPIT</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>

          <div className="mt-6 text-sm text-[var(--text-secondary)]">
            No account?{" "}
            <Link data-testid="login-register-link" to="/register" className="text-[var(--text-primary)] underline underline-offset-4 hover:text-[var(--accent)] transition-colors">
              Create one
            </Link>
          </div>

          {/* Demo credentials — subtle, not prominent */}
          <div className="mt-8 pt-5 border-t border-[var(--border)]">
            <div className="text-[10px] text-[var(--text-muted)] tracking-widest uppercase font-section mb-1.5">Demo access</div>
            <button
              type="button"
              className="text-[11px] font-mono-data text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors text-left"
              onClick={() => { setEmail("demo@algoforge.io"); setPassword("Demo@123"); }}
            >
              demo@algoforge.io / Demo@123
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
